import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  Plus, Search, Edit, Trash2, Eye, X, Users,
  Mail, Phone, MapPin, Calendar, User as UserIcon,
  GraduationCap, AlertTriangle
} from 'lucide-react';
import '../../styles/list-page.css';

const AVATAR_PALETTE = [
  ['#fef3d8', '#8a540a'],
  ['#e8f1e2', '#3b6b2e'],
  ['#fde6e6', '#a91e2c'],
  ['#e9e2ef', '#4b2a6b'],
  ['#e2ecf3', '#1f567d'],
  ['#f7e9d8', '#7a4618'],
];
const initialsOf = (s) =>
  `${(s.firstName || '').charAt(0)}${(s.lastName || '').charAt(0)}`.toUpperCase() || '?';
const colorFor = (key = '') => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};

const StudentsPage = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showModal, setShowModal] = useState(null); // null, 'add', 'edit', 'view'
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Filter and pagination state
  const [filters, setFilters] = useState({
    search: '',
    class: '',
    section: '',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
  });

  // Form state
  const [formData, setFormData] = useState({
    admissionNo: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'male',
    class: '1',
    section: 'A',
    rollNo: '',
    fatherName: '',
    motherName: '',
    contactPhone: '',
    contactEmail: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
  });

  // Fetch students on mount and when filters/pagination change
  useEffect(() => {
    fetchStudents();
  }, [filters, pagination.currentPage]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        ...(filters.search && { search: filters.search }),
        ...(filters.class && { class: filters.class }),
        ...(filters.section && { section: filters.section }),
      };

      const response = await api.get('/students', { params });
      const data = response.data.students;
      setStudents(Array.isArray(data) ? data : []);
      // Backend returns { pages, total } — not totalPages/totalCount.
      setPagination(prev => ({
        ...prev,
        totalPages: response.data.pagination?.pages || response.data.pagination?.totalPages || 1,
        totalCount: response.data.pagination?.total || response.data.pagination?.totalCount || 0,
      }));
    } catch (error) {
      console.error('Error fetching students:', error);
      // In production, show toast notification
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    setPagination(prev => ({
      ...prev,
      currentPage: 1, // Reset to first page on filter change
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      admissionNo: '',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: 'male',
      class: '1',
      section: 'A',
      rollNo: '',
      fatherName: '',
      motherName: '',
      contactPhone: '',
      contactEmail: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
    });
    setSelectedStudent(null);
  };

  const handleOpenAddModal = () => {
    resetForm();
    setShowModal('add');
  };

  const handleOpenEditModal = (student) => {
    setSelectedStudent(student);
    setFormData({
      admissionNo: student.admissionNo || '',
      firstName: student.firstName || '',
      lastName: student.lastName || '',
      dateOfBirth: student.dateOfBirth || '',
      gender: student.gender || 'male',
      class: student.class || '1',
      section: student.section || 'A',
      rollNo: student.rollNo || '',
      fatherName: student.fatherName || '',
      motherName: student.motherName || '',
      contactPhone: student.contactPhone || '',
      contactEmail: student.contactEmail || '',
      street: student.address?.street || '',
      city: student.address?.city || '',
      state: student.address?.state || '',
      zipCode: student.address?.zipCode || '',
    });
    setShowModal('edit');
  };

  const handleOpenViewModal = (student) => {
    setSelectedStudent(student);
    setShowModal('view');
  };

  const handleCloseModal = () => {
    setShowModal(null);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        admissionNo: formData.admissionNo,
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        class: formData.class,
        section: formData.section,
        rollNo: formData.rollNo,
        fatherName: formData.fatherName,
        motherName: formData.motherName,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
        },
      };

      if (showModal === 'add') {
        await api.post('/students', payload);
      } else if (showModal === 'edit') {
        await api.put(`/students/${selectedStudent.id}`, payload);
      }

      fetchStudents();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving student:', error);
      // In production, show error toast notification
    }
  };

  const handleDelete = (studentId) => {
    setDeleteConfirmId(studentId);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/students/${deleteConfirmId}`);
      fetchStudents();
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Error deleting student:', error);
      // In production, show error toast notification
    }
  };

  const handlePaginationChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({
        ...prev,
        currentPage: newPage,
      }));
    }
  };

  const classes = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const sections = ['A', 'B', 'C'];
  const genders = ['male', 'female', 'other'];

  const uniqueClasses = new Set(students.map(s => s.class).filter(Boolean)).size;
  const girlCount = students.filter(s => s.gender === 'female').length;

  return (
    <div className="tv-page">
      {/* Top bar */}
      <div className="tv-topbar">
        <div className="tv-titleblock">
          <span className="tv-eyebrow">People</span>
          <h1 className="tv-title">Students</h1>
          <p className="tv-sub">All enrolled students with classes, sections and contact info.</p>
        </div>

        <div className="tv-stats">
          <div className="tv-stat">
            <Users size={16} className="tv-stat-ic" />
            <div>
              <div className="tv-stat-num">{pagination.totalCount || students.length}</div>
              <div className="tv-stat-lbl">Students</div>
            </div>
          </div>
          <div className="tv-stat">
            <GraduationCap size={16} className="tv-stat-ic" />
            <div>
              <div className="tv-stat-num">{uniqueClasses}</div>
              <div className="tv-stat-lbl">Classes</div>
            </div>
          </div>
          <div className="tv-stat">
            <UserIcon size={16} className="tv-stat-ic" />
            <div>
              <div className="tv-stat-num">{girlCount}</div>
              <div className="tv-stat-lbl">On this page · girls</div>
            </div>
          </div>
        </div>

        <button className="tv-btn-primary" onClick={handleOpenAddModal}>
          <Plus size={16} />
          Add student
        </button>
      </div>

      {/* Toolbar */}
      <div className="tv-toolbar">
        <div className="tv-search">
          <Search size={16} className="tv-search-ic" />
          <input
            type="text"
            name="search"
            placeholder="Search by name, admission no, or roll no"
            value={filters.search}
            onChange={handleFilterChange}
          />
        </div>

        <select
          name="class"
          value={filters.class}
          onChange={handleFilterChange}
          className="tv-select"
        >
          <option value="">All classes</option>
          {classes.map(cls => (
            <option key={cls} value={cls}>{`Class ${cls}`}</option>
          ))}
        </select>

        <select
          name="section"
          value={filters.section}
          onChange={handleFilterChange}
          className="tv-select"
        >
          <option value="">All sections</option>
          {sections.map(sec => (
            <option key={sec} value={sec}>{`Section ${sec}`}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="tv-card">
        {loading ? (
          <div className="tv-loading">
            <div className="tv-spinner" />
            <span>Loading students…</span>
          </div>
        ) : students.length === 0 ? (
          <div className="tv-empty">
            <Users size={40} />
            <h3>No students yet</h3>
            <p>Add your first student or adjust filters to see records.</p>
            <button className="tv-btn-primary" onClick={handleOpenAddModal}>
              <Plus size={16} /> Add student
            </button>
          </div>
        ) : (
          <div className="tv-table-wrap">
            <table className="tv-table">
              <thead>
                <tr>
                  <th style={{ width: '28%' }}>Student</th>
                  <th style={{ width: '120px' }}>Class</th>
                  <th style={{ width: '90px' }}>Roll</th>
                  <th style={{ width: '110px' }}>Gender</th>
                  <th>Contact</th>
                  <th style={{ width: '130px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => {
                  const [bg, fg] = colorFor(student.admissionNo || student.id + '');
                  return (
                    <tr key={student.id}>
                      <td>
                        <div className="tv-person">
                          <span className="tv-avatar" style={{ background: bg, color: fg }}>
                            {initialsOf(student)}
                          </span>
                          <div className="tv-person-text">
                            <div className="tv-person-name">
                              {student.firstName} {student.lastName}
                            </div>
                            <div className="tv-person-meta">{student.admissionNo}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="tv-chips">
                          <span className="tv-chip tv-chip-class">{student.class}</span>
                          <span className="tv-chip tv-chip-section">{student.section}</span>
                        </div>
                      </td>
                      <td className="tv-num">{student.rollNo || '—'}</td>
                      <td>
                        <span className={`tv-chip tv-chip-gender tv-gender-${student.gender}`}>
                          {student.gender}
                        </span>
                      </td>
                      <td>
                        <div className="tv-contact">
                          <div className="tv-email">{student.contactEmail || '—'}</div>
                          <div className="tv-phone">{student.contactPhone || '—'}</div>
                        </div>
                      </td>
                      <td>
                        <div className="tv-actions">
                          <button
                            className="tv-icon-btn"
                            onClick={() => handleOpenViewModal(student)}
                            title="View"
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            className="tv-icon-btn"
                            onClick={() => handleOpenEditModal(student)}
                            title="Edit"
                          >
                            <Edit size={15} />
                          </button>
                          <button
                            className="tv-icon-btn tv-icon-danger"
                            onClick={() => handleDelete(student.id)}
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="tv-pagination">
            <span className="tv-pagination-info">
              Showing page {pagination.currentPage} of {pagination.totalPages}
              {pagination.totalCount > 0 && ` · ${pagination.totalCount} total`}
            </span>
            <div className="tv-pagination-actions">
              <button
                className="tv-btn-ghost"
                onClick={() => handlePaginationChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                Previous
              </button>
              <button
                className="tv-btn-ghost"
                onClick={() => handlePaginationChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {(showModal === 'add' || showModal === 'edit') && (
        <div className="tv-overlay" onClick={handleCloseModal}>
          <div className="tv-modal tv-modal-form" onClick={e => e.stopPropagation()}>
            <button className="tv-modal-close" onClick={handleCloseModal} aria-label="Close">
              <X size={18} />
            </button>
            <div className="tv-modal-head">
              <span className="tv-eyebrow">{showModal === 'add' ? 'Enrolment' : 'Edit profile'}</span>
              <h2 className="tv-modal-title">
                {showModal === 'add' ? 'Add a new student' : 'Edit student details'}
              </h2>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="tv-modal-body">
                <section className="tv-section">
                  <h4 className="tv-section-title">Identity</h4>
                  <div className="tv-form-grid">
                    <label className="tv-field">
                      <span>Admission no *</span>
                      <input
                        type="text" name="admissionNo" value={formData.admissionNo}
                        onChange={handleFormChange} required
                      />
                    </label>
                    <label className="tv-field">
                      <span>First name *</span>
                      <input
                        type="text" name="firstName" value={formData.firstName}
                        onChange={handleFormChange} required
                      />
                    </label>
                    <label className="tv-field">
                      <span>Last name *</span>
                      <input
                        type="text" name="lastName" value={formData.lastName}
                        onChange={handleFormChange} required
                      />
                    </label>
                    <label className="tv-field">
                      <span>Date of birth</span>
                      <input
                        type="date" name="dateOfBirth" value={formData.dateOfBirth}
                        onChange={handleFormChange}
                      />
                    </label>
                    <label className="tv-field">
                      <span>Gender</span>
                      <select name="gender" value={formData.gender} onChange={handleFormChange}>
                        {genders.map(g => (
                          <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                </section>

                <section className="tv-section">
                  <h4 className="tv-section-title">Academic</h4>
                  <div className="tv-form-grid">
                    <label className="tv-field">
                      <span>Class *</span>
                      <select name="class" value={formData.class} onChange={handleFormChange} required>
                        {classes.map(cls => (
                          <option key={cls} value={cls}>{`Class ${cls}`}</option>
                        ))}
                      </select>
                    </label>
                    <label className="tv-field">
                      <span>Section *</span>
                      <select name="section" value={formData.section} onChange={handleFormChange} required>
                        {sections.map(sec => (
                          <option key={sec} value={sec}>{`Section ${sec}`}</option>
                        ))}
                      </select>
                    </label>
                    <label className="tv-field">
                      <span>Roll no *</span>
                      <input
                        type="text" name="rollNo" value={formData.rollNo}
                        onChange={handleFormChange} required
                      />
                    </label>
                  </div>
                </section>

                <section className="tv-section">
                  <h4 className="tv-section-title">Parents</h4>
                  <div className="tv-form-grid">
                    <label className="tv-field">
                      <span>Father's name</span>
                      <input
                        type="text" name="fatherName" value={formData.fatherName}
                        onChange={handleFormChange}
                      />
                    </label>
                    <label className="tv-field">
                      <span>Mother's name</span>
                      <input
                        type="text" name="motherName" value={formData.motherName}
                        onChange={handleFormChange}
                      />
                    </label>
                  </div>
                </section>

                <section className="tv-section">
                  <h4 className="tv-section-title">Contact</h4>
                  <div className="tv-form-grid">
                    <label className="tv-field">
                      <span>Phone *</span>
                      <input
                        type="tel" name="contactPhone" value={formData.contactPhone}
                        onChange={handleFormChange} required
                      />
                    </label>
                    <label className="tv-field">
                      <span>Email *</span>
                      <input
                        type="email" name="contactEmail" value={formData.contactEmail}
                        onChange={handleFormChange} required
                      />
                    </label>
                  </div>
                </section>

                <section className="tv-section">
                  <h4 className="tv-section-title">Address</h4>
                  <div className="tv-form-grid">
                    <label className="tv-field tv-field-full">
                      <span>Street</span>
                      <input
                        type="text" name="street" value={formData.street}
                        onChange={handleFormChange}
                      />
                    </label>
                    <label className="tv-field">
                      <span>City</span>
                      <input
                        type="text" name="city" value={formData.city}
                        onChange={handleFormChange}
                      />
                    </label>
                    <label className="tv-field">
                      <span>State</span>
                      <input
                        type="text" name="state" value={formData.state}
                        onChange={handleFormChange}
                      />
                    </label>
                    <label className="tv-field">
                      <span>Zip code</span>
                      <input
                        type="text" name="zipCode" value={formData.zipCode}
                        onChange={handleFormChange}
                      />
                    </label>
                  </div>
                </section>
              </div>

              <div className="tv-modal-footer">
                <button type="button" className="tv-btn-ghost" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="tv-btn-primary">
                  {showModal === 'add' ? 'Add student' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showModal === 'view' && selectedStudent && (() => {
        const [bg, fg] = colorFor(selectedStudent.admissionNo || selectedStudent.id + '');
        const addr = selectedStudent.address || {};
        const hasAddr = addr.street || addr.city || addr.state || addr.zipCode;
        return (
          <div className="tv-overlay" onClick={handleCloseModal}>
            <div className="tv-modal tv-modal-view" onClick={e => e.stopPropagation()}>
              <button className="tv-modal-close" onClick={handleCloseModal} aria-label="Close">
                <X size={18} />
              </button>

              <div className="tv-modal-hero">
                <span className="tv-modal-avatar" style={{ background: bg, color: fg }}>
                  {initialsOf(selectedStudent)}
                </span>
                <div className="tv-modal-hero-text">
                  <div className="tv-modal-name">
                    {selectedStudent.firstName} {selectedStudent.lastName}
                  </div>
                  <div className="tv-modal-meta">
                    <span className="tv-modal-pill">{selectedStudent.admissionNo}</span>
                    <span className="tv-modal-pill-quiet">
                      Class {selectedStudent.class}-{selectedStudent.section}
                    </span>
                    <span className="tv-modal-pill-quiet">Roll {selectedStudent.rollNo}</span>
                    {selectedStudent.gender && (
                      <span className="tv-modal-pill-quiet">
                        {selectedStudent.gender.charAt(0).toUpperCase() + selectedStudent.gender.slice(1)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="tv-modal-body">
                <section className="tv-section">
                  <h4 className="tv-section-title">Contact</h4>
                  <div className="tv-infogrid">
                    <div className="tv-info">
                      <Mail size={15} className="tv-info-ic" />
                      <div>
                        <div className="tv-info-lbl">Email</div>
                        <div className="tv-info-val">{selectedStudent.contactEmail || '—'}</div>
                      </div>
                    </div>
                    <div className="tv-info">
                      <Phone size={15} className="tv-info-ic" />
                      <div>
                        <div className="tv-info-lbl">Phone</div>
                        <div className="tv-info-val">{selectedStudent.contactPhone || '—'}</div>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="tv-section">
                  <h4 className="tv-section-title">Profile</h4>
                  <div className="tv-infogrid">
                    <div className="tv-info">
                      <Calendar size={15} className="tv-info-ic" />
                      <div>
                        <div className="tv-info-lbl">Date of birth</div>
                        <div className="tv-info-val">{selectedStudent.dateOfBirth || '—'}</div>
                      </div>
                    </div>
                    <div className="tv-info">
                      <UserIcon size={15} className="tv-info-ic" />
                      <div>
                        <div className="tv-info-lbl">Father</div>
                        <div className="tv-info-val">{selectedStudent.fatherName || '—'}</div>
                      </div>
                    </div>
                    <div className="tv-info">
                      <UserIcon size={15} className="tv-info-ic" />
                      <div>
                        <div className="tv-info-lbl">Mother</div>
                        <div className="tv-info-val">{selectedStudent.motherName || '—'}</div>
                      </div>
                    </div>
                  </div>
                </section>

                {hasAddr && (
                  <section className="tv-section">
                    <h4 className="tv-section-title">Address</h4>
                    <div className="tv-info">
                      <MapPin size={15} className="tv-info-ic" />
                      <div>
                        <div className="tv-info-val">
                          {[addr.street, addr.city, addr.state, addr.zipCode]
                            .filter(Boolean).join(', ')}
                        </div>
                      </div>
                    </div>
                  </section>
                )}
              </div>

              <div className="tv-modal-footer">
                <button className="tv-btn-ghost" onClick={handleCloseModal}>Close</button>
                <button
                  className="tv-btn-primary"
                  onClick={() => {
                    handleOpenEditModal(selectedStudent);
                  }}
                >
                  <Edit size={15} />
                  Edit student
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="tv-overlay" onClick={() => setDeleteConfirmId(null)}>
          <div className="tv-modal tv-modal-confirm" onClick={e => e.stopPropagation()}>
            <button className="tv-modal-close" onClick={() => setDeleteConfirmId(null)} aria-label="Close">
              <X size={18} />
            </button>
            <div className="tv-confirm-body">
              <div className="tv-confirm-icon">
                <AlertTriangle size={20} />
              </div>
              <div>
                <h3 className="tv-confirm-title">Delete this student?</h3>
                <p className="tv-confirm-text">
                  This will permanently remove the student record and all linked marks,
                  attendance and fees. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="tv-modal-footer">
              <button
                className="tv-btn-ghost"
                onClick={() => setDeleteConfirmId(null)}
              >
                Cancel
              </button>
              <button className="tv-btn-danger" onClick={confirmDelete}>
                <Trash2 size={15} /> Delete student
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsPage;
